import { useState, type FormEvent } from 'react';
import { Button, Card, ErrorNotice, Field, Icon, StatusPill } from '@hive/ui';
import { api, baseUrl, errorMessage, type ApiKey, type ApiScope } from '../api/client';
import { useLoad, useNow } from '../api/hooks';
import { Dialog } from '../components/Dialog';
import { Shell } from '../components/Shell';
import { CrmFeedsCard } from '../components/CrmFeedsCard';
import { WebhooksCard } from '../components/WebhooksCard';
import { ago } from '../format';

const SCOPES: { scope: ApiScope; label: string; hint: string }[] = [
  { scope: 'venues:read', label: 'Read venues', hint: 'Venues, screens, licences, rights and the catalogue' },
  { scope: 'venues:write', label: 'Write venues', hint: 'Create, update and deactivate venues and screens' },
  { scope: 'rights:write', label: 'Write rights', hint: 'Licences and rights' },
];

/** API keys for operator CRMs, the request log, and developer pointers. */
export function IntegrationsPage() {
  const now = useNow(30_000);
  const keys = useLoad(async () => (await api.GET('/v1/integrations/keys')).data);
  const requests = useLoad(async () => (await api.GET('/v1/integrations/requests', { params: { query: { limit: 50 } } })).data);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function revoke(key: ApiKey) {
    const { error: err } = await api.DELETE('/v1/integrations/keys/{keyId}', { params: { path: { keyId: key.id } } });
    setRevoking(null);
    if (err) return setError(errorMessage(err));
    void keys.reload();
  }

  const active = (keys.data ?? []).filter((k) => !k.revokedAt);
  const revoked = (keys.data ?? []).filter((k) => k.revokedAt);

  return (
    <Shell network={null} live>
      <main className="mc-page">
        <div className="mc-pagehead">
          <div className="mc-pagehead__left">
            <h1 className="mc-h1">Integrations</h1>
            <span className="mc-pill-soft">Operator CRMs and back-office systems</span>
          </div>
        </div>
        {error && <ErrorNotice title={error} />}

        <div className="mc-admin-grid">
          <div className="mc-col">
            <Card className="mc-table-card">
              <div className="mc-toolbar">
                <span className="mc-section-title">
                  <Icon name="key" size={16} />
                  &nbsp;API keys
                </span>
                <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                  <Icon name="plus" size={16} />
                  Create key
                </Button>
              </div>
              <table className="mc-table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Scopes</th>
                    <th scope="col">Created</th>
                    <th scope="col">Last used</th>
                    <th scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {active.map((k) => (
                    <tr key={k.id}>
                      <td>
                        <span className="mc-table__who">
                          <span className="mc-strong">{k.name}</span>
                          <code className="mc-tiny hv-muted">{k.prefix}_…</code>
                        </span>
                      </td>
                      <td className="mc-small">{k.scopes.join(', ')}</td>
                      <td className="mc-small">
                        {ago(k.createdAt, now)} ago · {k.createdBy}
                      </td>
                      <td className="mc-small">{k.lastUsedAt ? `${ago(k.lastUsedAt, now)} ago` : 'Never'}</td>
                      <td>
                        <Button variant="link" className="mc-link-danger" onClick={() => setRevoking(k)}>
                          Revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {keys.data && active.length === 0 && (
                    <tr>
                      <td colSpan={5} className="mc-table__empty">
                        No keys yet. Create one for each system that connects, so each can be revoked on its own.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {revoked.length > 0 && (
                <details className="mc-details mc-pad">
                  <summary className="mc-small">Revoked keys ({revoked.length})</summary>
                  <ul className="mc-rows">
                    {revoked.map((k) => (
                      <li key={k.id} className="mc-rows__row">
                        <span className="mc-grow">
                          <span>{k.name}</span>
                          <span className="mc-tiny hv-muted">{k.prefix}_…</span>
                        </span>
                        <StatusPill status="idle">Revoked {k.revokedAt ? `${ago(k.revokedAt, now)} ago` : ''}</StatusPill>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </Card>

            <WebhooksCard />

            <CrmFeedsCard />

            <Card className="mc-table-card">
              <div className="mc-toolbar">
                <span className="mc-section-title">
                  <Icon name="history" size={16} />
                  &nbsp;Recent calls
                </span>
                <Button size="sm" onClick={() => void requests.reload()}>
                  Refresh
                </Button>
              </div>
              <table className="mc-table mc-table--compact">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Key</th>
                    <th scope="col">Call</th>
                    <th scope="col">Result</th>
                    <th scope="col">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {(requests.data ?? []).map((r) => (
                    <tr key={r.id}>
                      <td className="mc-small">{ago(r.at, now)} ago</td>
                      <td className="mc-small">{r.keyName}</td>
                      <td className="mc-small">
                        <code>
                          {r.method} {r.path.replace(/^\/integration/, '')}
                        </code>
                        {r.message && <span className="mc-tiny hv-muted mc-line">{r.message}</span>}
                      </td>
                      <td className={r.status >= 400 ? 'mc-bad' : 'mc-good'}>{r.status}</td>
                      <td className="mc-small">{r.durationMs} ms</td>
                    </tr>
                  ))}
                  {requests.data && requests.data.length === 0 && (
                    <tr>
                      <td colSpan={5} className="mc-table__empty">
                        No calls yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>

          <div className="mc-col">
            <Card padded className="mc-side-card">
              <span className="mc-section-title">For developers</span>
              <p className="mc-small">
                The CRM calls Hive to create and update venues, screens, licences and rights, using its own IDs. Every write is an idempotent
                PUT, so retries are safe. Hive calls the CRM back through webhooks when anything changes, including changes made here.
              </p>
              <div className="mc-kv">
                <span className="hv-muted">Base URL</span>
                <code className="mc-small">{baseUrl}/integration</code>
                <span className="hv-muted">Contract</span>
                <a className="mc-small" href={`${baseUrl}/integration/openapi.yaml`} target="_blank" rel="noreferrer">
                  openapi.yaml
                </a>
                <span className="hv-muted">Limit</span>
                <span className="mc-small">120 calls a minute per key</span>
              </div>
              <pre className="mc-code">{`curl -X PUT ${baseUrl}/integration/v1/venues/CRM-1042 \\
  -H "Authorization: Bearer hive_…" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"The Local Tap","timezone":"Asia/Dubai",
       "screens":[{"code":"LT-1","name":"Bar"}]}'`}</pre>
            </Card>
          </div>
        </div>
      </main>

      {creating && <CreateKeyDialog onClose={() => setCreating(false)} onCreated={() => void keys.reload()} />}
      {revoking && (
        <div className="mc-scrim" onClick={() => setRevoking(null)}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="revoke-title" className="mc-dialog" style={{ width: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="mc-dialog__head">
              <h1 id="revoke-title" className="mc-dialog__title">
                Revoke {revoking.name}?
              </h1>
            </div>
            <div className="mc-dialog__body">
              <p className="mc-small">Calls with this key fail straight away. This cannot be undone; create a new key if it is needed again.</p>
            </div>
            <div className="mc-dialog__foot">
              <Button onClick={() => setRevoking(null)}>Keep</Button>
              <Button variant="primary" className="mc-btn-danger" onClick={() => revoke(revoking)}>
                Revoke key
              </Button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function CreateKeyDialog({ onClose, onCreated }: { onClose(): void; onCreated(): void }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>(['venues:read', 'venues:write', 'rights:write']);
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!scopes.length) return setError('Choose at least one scope.');
    setBusy(true);
    setError(null);
    const { data, error: err } = await api.POST('/v1/integrations/keys', { body: { name: name.trim(), scopes } });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    setSecret(data.secret);
    onCreated();
  }

  async function copy() {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Dialog
      titleId="key-title"
      title={secret ? 'Copy the key now' : 'Create an API key'}
      subtitle={secret ? 'It is shown only once. Hive keeps just a fingerprint of it.' : 'One key per system, so each can be revoked on its own.'}
      onClose={onClose}
      width={560}
      footer={
        secret ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" form="key-form" disabled={busy}>
              Create key
            </Button>
          </>
        )
      }
    >
      {secret ? (
        <div className="mc-form">
          <code className="mc-secret" aria-label="API key">
            {secret}
          </code>
          <div className="mc-inline">
            <Button onClick={copy}>{copied ? 'Copied' : 'Copy key'}</Button>
            <span className="mc-small hv-muted">Store it in the CRM&apos;s secret settings, not in code or email.</span>
          </div>
        </div>
      ) : (
        <form id="key-form" className="mc-form" onSubmit={submit}>
          {error && <ErrorNotice title={error} />}
          <Field id="k-name" label="Name" placeholder="Salesforce sync" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />
          <fieldset className="mc-fieldset">
            <legend className="hv-field__label">Scopes</legend>
            {SCOPES.map((s) => (
              <label key={s.scope} className="mc-check">
                <input
                  type="checkbox"
                  checked={scopes.includes(s.scope)}
                  onChange={(e) => setScopes((list) => (e.target.checked ? [...list, s.scope] : list.filter((x) => x !== s.scope)))}
                />
                <span>
                  <span className="mc-strong">{s.label}</span> <code className="mc-tiny hv-muted">{s.scope}</code>
                  <span className="mc-small hv-muted mc-line">{s.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </form>
      )}
    </Dialog>
  );
}
